import { RootState } from '../../../types';
import { EntityTypes } from '../../../constants/entities';
import { IEntityReducer } from 'core/reducers/entities';
import { getEntities } from '../utils/getEntities';
import {
  CourseProgressLogType,
  CourseProgressStatus,
  ICourseProgress,
  ICourseProgressDAO,
  ICourseProgressLog,
} from 'types/domain/permissions';
import { ISearch, SearchType, SortBy } from 'types/domain/search';
import { parseUserDAO } from './users';
import { convertDate, convertDateUTC } from 'utils/convertDate';
import { reduceArrayById } from 'utils/reduceArrayById';
import { IReportReducer, ReportType } from 'core/slices/report';
import { parseGroupUser } from './groupUsers';
import { createCachedSelector } from 're-reselect';
import { parseModule } from '../product/module';
import { IOrgUserCertificate, IOrgUserVariable, IOrgUserVariableDAO } from 'types/domain/user';
import { ISessionAttempt } from 'types/domain/session';
import { IEmail } from 'types/domain/notifications';
import { parseVariableDAO } from './variable';
import { parseCourseNodeDAO } from './courseNodes';

const entity = (state: RootState) => state.entity.present;
const id = (state: RootState, id?: number) => (id ? id : state.state[EntityTypes.COURSE]);
const search = (state: RootState) => state.core.search;
const report = (state: RootState) => state.core.report;

export const selectCourseProgress = createCachedSelector(
  [entity, id, search, report],
  (entity: IEntityReducer, id: number, search: ISearch, report: IReportReducer) => {
    return getEntities(entity, EntityTypes.COURSE_PROGRESS, 'courseId', id)
      .filter((courseProgress: ICourseProgress) => courseProgress.status > -1)
      .map((courseProgress: ICourseProgress) => parseCourseProgress(entity, courseProgress.id))
      .filter((cp: ICourseProgressDAO) =>
        search.searchType === SearchType.COURSE_PROGRESS && search.term && cp.organisationUser
          ? (cp.organisationUser.firstName &&
              cp.organisationUser.firstName.toLowerCase().includes(search.term.toLocaleLowerCase())) ||
            (cp.organisationUser.lastName &&
              cp.organisationUser.lastName.toLowerCase().includes(search.term.toLocaleLowerCase())) ||
            (cp.organisationUser.email &&
              cp.organisationUser.email.toLowerCase().includes(search.term.toLocaleLowerCase()))
          : true,
      )
      .filter(cp =>
        search.sort !== null ? (search.sort === SortBy.NAME ? !!cp.organisationUser?.name : !!cp.date) : true,
      )
      .filter((cp: ICourseProgressDAO) =>
        report.custom.reportType === ReportType.COURSE_PROGRESS && report.custom.filter.status
          ? StatusFilter(cp, report.custom.filter.status)
          : true,
      )
      .filter((cp: ICourseProgressDAO) =>
        report.custom.reportType === ReportType.COURSE_PROGRESS && report.custom.filter.start
          ? convertDateUTC(cp.modified?.date) > report.custom.filter.start
          : true,
      )
      .filter((cp: ICourseProgressDAO) =>
        report.custom.reportType === ReportType.COURSE_PROGRESS && report.custom.filter.end
          ? convertDateUTC(cp.modified?.date) < report.custom.filter.end
          : true,
      )
      .filter((cp: ICourseProgressDAO) =>
        report.custom.reportType === ReportType.COURSE_PROGRESS && report.custom.filter.groupId
          ? cp.organisationUser.groupUsers.some(gu => gu.groupId === report.custom.filter.groupId)
          : true,
      )
      .filter((cp: ICourseProgressDAO) =>
        report.custom.reportType === ReportType.COURSE_PROGRESS && report.custom.filter.variableId
          ? !!cp.organisationUserVariables[report.custom.filter.variableId]
          : true,
      )
      .sort((a, b) => sortByNameModified(a, b, search));
  },
)((state, id) => id || state.state[EntityTypes.COURSE] || 'unknown');

export function parseCourseProgress(entity, id) {
  const courseProgress: ICourseProgress = entity[EntityTypes.COURSE_PROGRESS][id];
  // const organisationUser = parseUserDAO(entity, courseProgress.organisationUserId);
  if (!courseProgress) return null;

  const organisationUserVariables = reduceArrayById(
    getEntities(entity, EntityTypes.ORG_USER_VARIABLE, 'courseProgressId', id).map((orgUserVar: IOrgUserVariable) => {
      return {
        ...orgUserVar,
        text: orgUserVar.textId ? entity[EntityTypes.TEXT][orgUserVar.textId] : null,
      } as Partial<IOrgUserVariableDAO>;
    }),
    'variableId',
  );
  return {
    ...courseProgress,
    date: courseProgress.modified?.date || courseProgress.created?.date,
    organisationUserVariables,
    organisationUser: parseUserDAO(entity, courseProgress.organisationUserId),
    lastOrganisationUserVariable: courseProgress.lastOrganisationUserVariableId
      ? entity[EntityTypes.ORG_USER_VARIABLE][courseProgress.lastOrganisationUserVariableId]
      : null,
    sessionAttempt: courseProgress.sessionAttemptId
      ? entity[EntityTypes.SESSION_ATTEMPT][courseProgress.sessionAttemptId]
      : null,
    module: courseProgress.moduleId ? parseModule(entity, courseProgress.moduleId) : null,
    lastGroupUser: courseProgress.lastGroupUserId ? parseGroupUser(entity, courseProgress.lastGroupUserId) : null,
    courseOrgUserVariable: organisationUserVariables[entity[EntityTypes.COURSE][courseProgress.courseId]?.variableId],
    lastEmail: courseProgress.lastEmailId ? entity[EntityTypes.EMAIL][courseProgress.lastEmailId] : null,
    log: parseCourseProgressLog(entity, id, entity[EntityTypes.COURSE][courseProgress.courseId]?.variableId),
  } as ICourseProgressDAO;
}

function parseCourseProgressLog(entity, id, courseVariableId) {
  const orgUserVars = getEntities(entity, EntityTypes.ORG_USER_VARIABLE, 'courseProgressId', id)
    .filter(ouv => ouv.variableId !== courseVariableId)
    .map((orgUserVariable: IOrgUserVariable) => {
      return {
        type: CourseProgressLogType.ORG_USER_VARIABLE,
        created: orgUserVariable.created,
        modified: orgUserVariable.modified,
        orgUserVariable: {
          ...orgUserVariable,
          courseNode: parseCourseNodeDAO(entity, orgUserVariable.courseNodeId),
          text: orgUserVariable.textId ? entity[EntityTypes.TEXT][orgUserVariable.textId] : null,
          variable: parseVariableDAO(entity, orgUserVariable.variableId),
        } as IOrgUserVariableDAO,
      } as ICourseProgressLog;
    });

  return [
    ...getEntities(entity, EntityTypes.SESSION_ATTEMPT, 'courseProgressId', id).map(
      (sessionAttempt: ISessionAttempt) => {
        return {
          type: CourseProgressLogType.SESSION_ATTEMPT,
          created: sessionAttempt.created,
          modified: sessionAttempt.modified,
          sessionAttempt,
          orgUserVariable: orgUserVars.find(orv => orv.orgUserVariable.variable.moduleId === sessionAttempt.moduleId)
            ?.orgUserVariable,
        } as ICourseProgressLog;
      },
    ),
    ...orgUserVars.filter(orv => !orv.orgUserVariable.variable?.moduleId),
    ...getEntities(entity, EntityTypes.ORG_USER_CERTIFICATE, 'courseProgressId', id).map(
      (orgUserCertificate: IOrgUserCertificate) => {
        return {
          type: CourseProgressLogType.ORG_USER_CERTIFICATE,
          created: orgUserCertificate.created,
          modified: orgUserCertificate.modified,
          orgUserCertificate,
        } as ICourseProgressLog;
      },
    ),
    ...getEntities(entity, EntityTypes.EMAIL, 'courseProgressId', id).map((email: IEmail) => {
      return {
        type: CourseProgressLogType.EMAIL,
        created: email.created,
        modified: email.modified,
        email,
      } as ICourseProgressLog;
    }),
  ].sort(sortByDateModified) as ICourseProgressLog[];
}

function StatusFilter(cp: ICourseProgressDAO, status: CourseProgressStatus) {
  switch (status) {
    case CourseProgressStatus.COMPLETE:
      return !!cp.courseOrgUserVariable;
    case CourseProgressStatus.DORMANT:
    case CourseProgressStatus.ACTIVE:
      return [CourseProgressStatus.ACTIVE, CourseProgressStatus.DORMANT].includes(cp.status);
  }
}

export function sortByNameModified(a: ICourseProgressDAO, b: ICourseProgressDAO, search: ISearch) {
  switch (search.sort) {
    case SortBy.NAME:
      if (a.organisationUser.name == null) return -1;
      if (b.organisationUser.name == null) return 1;
      return a.organisationUser.name.localeCompare(b.organisationUser.name);
    default:
      if (a.date == null) return 1;
      if (b.date == null) return -1;

      return convertDate(a.date) < convertDate(b.date) ? 1 : -1;
  }
}

export function sortByDateModified(a: ICourseProgressLog, b: ICourseProgressLog) {
  const aDate = a.modified?.date || a.created?.date;
  const bDate = b.modified?.date || b.created?.date;

  if (aDate == null) return 1;
  if (bDate == null) return -1;

  return convertDate(aDate) < convertDate(bDate) ? -1 : 1;
}
